import { IMessageItem } from "@/types/component";
import { buildMsgItem } from "@/components/mobile/chat/mode/util";
import { useChat } from "@/stores/chat";
import { useMate } from "@/stores/mate";
import { ChatMessage } from "@/types/entity";
import { useDebounceFn, useScroll } from "@vueuse/core";
import {
  Ref,
  ref,
  nextTick,
  onMounted,
  computed,
  watch,
  ComputedRef,
} from "vue";
export const useChatMessageList = ({
  sessionId,
  messageContainerRef,
  scrollRef,
  myMessage,
  streamMsg,
  isStreamReplying,
}: {
  sessionId: number;
  messageContainerRef: Ref<HTMLElement | null>;
  scrollRef: Ref<HTMLDivElement | null>;
  myMessage: Ref<string> | ComputedRef<string>;
  streamMsg: Ref<string> | ComputedRef<string>;
  isStreamReplying: Ref<boolean> | ComputedRef<boolean>;
}) => {
  const chat = useChat();
  const mateStore = useMate();

  const messageList = computed<IMessageItem[]>(() => {
    const messageList: ChatMessage[] = chat.messageListBySessionId(sessionId);
    return [...messageList]
      .reverse()
      .map((item) => buildMsgItem(item, mateStore.currentMate!.id!));
  });

  const scrollLoading = ref(false);
  const finished = ref(false);
  const error = ref(false);
  const scrollLoad = async () => {
    if (!scrollRef.value) return;
    try {
      scrollLoading.value = true;
      const oldScrollTop = scrollRef.value.scrollTop;
      const { hasMore, count } = await chat.fetchOlderMessageList(sessionId);
      await nextTick(() => {
        let offset = 0;
        for (let i = 0; i < count; i++) {
          const item = messageContainerRef.value!.children[i];
          offset += item.clientHeight;
        }
        scrollRef.value!.scrollTop = offset + oldScrollTop;
      });
      finished.value = !hasMore;
      scrollLoading.value = false;
    } catch (e) {
      error.value = true;
      throw e;
    } finally {
      scrollLoading.value = false;
    }
  };

  const { y: chatScrollY, isScrolling } = useScroll(scrollRef, {
    behavior: "smooth",
    offset: { bottom: 20 },
  });

  function scrollToBottom(onlyIfArrivedBottom = false) {
    if (!scrollRef.value || isScrolling.value) return;
    const offsetHeight =
      scrollRef.value.scrollHeight - scrollRef.value.clientHeight;
    const isBottom = offsetHeight - chatScrollY.value < 200;
    if (onlyIfArrivedBottom && !isBottom) return;
    chatScrollY.value = scrollRef.value.scrollHeight;
  }

  const debounceScrollToBottom = useDebounceFn(scrollToBottom, 100);

  onMounted(() => {
    // 进入页面时，滚动到底部
    debounceScrollToBottom();
  });

  watch(myMessage, (myMessage) => myMessage && debounceScrollToBottom(), {
    flush: "post",
  });
  // 接收stream消息时，滚动条接近底部时，滚动到底部
  watch(streamMsg, (streamMsg) => streamMsg && scrollToBottom(true), {
    flush: "post",
  });

  watch(isStreamReplying, (isStreamReplying) => {
    if (isStreamReplying) {
      nextTick(() => debounceScrollToBottom(true));
    }
  });

  return {
    messageList,
    scrollLoading,
    finished,
    error,
    scrollLoad,
    scrollToBottom,
    debounceScrollToBottom,
  };
};
